<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { recordingApiService } from '@/services/recording.service'
import type { RecordingListItem, RecordingListFilter } from '@/services/recording.service'

const recordings = ref<RecordingListItem[]>([])
const loading = ref(false)
const errorMessage = ref('')

// 필터
const filterGcs = ref('')
const filterDrone = ref('')
const filterStatus = ref('')
const filterAfter = ref('')   // YYYY-MM-DD (로컬타임)
const filterBefore = ref('')  // YYYY-MM-DD (로컬타임)

// 미리보기 모달
const previewRecording = ref<RecordingListItem | null>(null)
const previewUrl = computed(() =>
  previewRecording.value ? recordingApiService.downloadUrl(previewRecording.value.recordingId) : '',
)

const STATUS_LABEL: Record<string, string> = {
  recording: '녹화 중',
  starting: '시작 중',
  stopping: '중지 중',
  completed: '완료',
  failed: '실패',
  orphan: '손상(부분 파일)',
}

const STATUS_COLOR: Record<string, string> = {
  recording: '#dc3545',
  starting: '#ffc107',
  stopping: '#ffc107',
  completed: '#28a745',
  failed: '#6c757d',
  orphan: '#fd7e14',
}

function localDateToEpochMs(s: string, endOfDay = false): number | undefined {
  if (!s) return undefined
  // YYYY-MM-DD
  const [y, m, d] = s.split('-').map(n => parseInt(n, 10))
  if (!y || !m || !d) return undefined
  const dt = endOfDay
    ? new Date(y, m - 1, d, 23, 59, 59, 999)
    : new Date(y, m - 1, d, 0, 0, 0, 0)
  return dt.getTime()
}

function formatLocalDateTime(epochMs: number): string {
  if (!epochMs) return '-'
  const d = new Date(epochMs)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function formatDuration(ms: number): string {
  if (!ms || ms < 0) return '-'
  const sec = Math.floor(ms / 1000)
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${m}:${String(s).padStart(2, '0')}`
}

function formatBytes(b: number): string {
  if (!b || b < 0) return '-'
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`
}

async function loadList() {
  loading.value = true
  errorMessage.value = ''
  try {
    const filter: RecordingListFilter = {
      gcsId: filterGcs.value || undefined,
      droneName: filterDrone.value || undefined,
      status: filterStatus.value || undefined,
      after: localDateToEpochMs(filterAfter.value, false),
      before: localDateToEpochMs(filterBefore.value, true),
    }
    recordings.value = await recordingApiService.list(filter)
  } catch (err: any) {
    errorMessage.value = `녹화 목록 조회 실패: ${err?.response?.data?.error || err?.message || '알 수 없는 오류'}`
  } finally {
    loading.value = false
  }
}

async function onClickDelete(rec: RecordingListItem) {
  if (!confirm(`녹화를 삭제하시겠습니까?\n\n파일: ${rec.filename}\n캐스트룸: ${rec.castroomId.gcs}/${rec.castroomId.drone}/${rec.castroomId.camera}`)) {
    return
  }
  try {
    await recordingApiService.deleteById(rec.recordingId)
    recordings.value = recordings.value.filter(r => r.recordingId !== rec.recordingId)
  } catch (err: any) {
    errorMessage.value = `녹화 삭제 실패: ${err?.response?.data?.error || err?.message || '알 수 없는 오류'}`
  }
}

function onClickPreview(rec: RecordingListItem) {
  if (rec.status !== 'completed') {
    errorMessage.value = '완료된 녹화만 미리보기 가능합니다.'
    setTimeout(() => { errorMessage.value = '' }, 3000)
    return
  }
  previewRecording.value = rec
}

function closePreview() {
  previewRecording.value = null
}

function isDownloadable(rec: RecordingListItem): boolean {
  // 'orphan'도 부분 파일 다운로드는 허용 (검증 목적). 나머지 활성 상태는 차단.
  return rec.status === 'completed' || rec.status === 'orphan' || rec.status === 'failed'
}

onMounted(() => {
  loadList()
})
</script>

<template>
  <main class="page">
    <h1 class="page-title">녹화 영상 관리</h1>

    <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>

    <!-- 필터 -->
    <section class="filters">
      <div class="filter-row">
        <label>GCS <input v-model="filterGcs" placeholder="예: KETI_GCS" /></label>
        <label>드론 <input v-model="filterDrone" placeholder="예: drone01" /></label>
        <label>상태
          <select v-model="filterStatus">
            <option value="">전체</option>
            <option value="recording">녹화 중</option>
            <option value="completed">완료</option>
            <option value="failed">실패</option>
            <option value="orphan">손상(부분 파일)</option>
          </select>
        </label>
        <label>시작일(이후) <input v-model="filterAfter" type="date" /></label>
        <label>시작일(이전) <input v-model="filterBefore" type="date" /></label>
        <button class="btn btn-primary" :disabled="loading" @click="loadList">{{ loading ? '조회 중...' : '조회' }}</button>
      </div>
    </section>

    <!-- 결과 -->
    <section class="result">
      <p class="meta">총 {{ recordings.length }}건</p>
      <table class="recording-table">
        <thead>
          <tr>
            <th>시작 시각</th>
            <th>캐스트룸</th>
            <th>길이</th>
            <th>크기</th>
            <th>포맷</th>
            <th>미디어 서버</th>
            <th>트리거</th>
            <th>상태</th>
            <th>작업</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!recordings.length && !loading">
            <td colspan="9" class="empty">녹화 영상이 없습니다.</td>
          </tr>
          <tr v-for="rec in recordings" :key="rec.recordingId">
            <td>{{ formatLocalDateTime(rec.startedAt) }}</td>
            <td>{{ rec.castroomId.gcs }} / {{ rec.castroomId.drone }} / {{ rec.castroomId.camera }}</td>
            <td>{{ formatDuration(rec.durationMs) }}</td>
            <td>{{ formatBytes(rec.fileSize) }}</td>
            <td>{{ rec.format }}</td>
            <td>{{ rec.mediaServerType }}</td>
            <td>{{ rec.trigger }}</td>
            <td>
              <span class="status-pill" :style="{ background: STATUS_COLOR[rec.status] || '#999' }">
                {{ STATUS_LABEL[rec.status] || rec.status }}
              </span>
            </td>
            <td class="actions">
              <button class="btn btn-sm btn-outline"
                :disabled="rec.status !== 'completed'"
                @click="onClickPreview(rec)">미리보기</button>
              <a v-if="isDownloadable(rec)"
                :href="recordingApiService.downloadUrl(rec.recordingId)"
                :download="rec.filename"
                class="btn btn-sm btn-primary">다운로드</a>
              <span v-else class="btn btn-sm btn-disabled">다운로드</span>
              <button class="btn btn-sm btn-danger" @click="onClickDelete(rec)">삭제</button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <!-- 미리보기 모달 -->
    <div v-if="previewRecording" class="modal-backdrop" @click.self="closePreview">
      <div class="modal">
        <div class="modal-header">
          <h3>{{ previewRecording.filename }}</h3>
          <button class="btn-close" @click="closePreview">✕</button>
        </div>
        <video :src="previewUrl" controls preload="metadata" class="preview-video"></video>
        <div class="modal-meta">
          <span>{{ previewRecording.castroomId.gcs }} / {{ previewRecording.castroomId.drone }} / {{ previewRecording.castroomId.camera }}</span>
          <span>{{ formatLocalDateTime(previewRecording.startedAt) }}</span>
          <span>길이: {{ formatDuration(previewRecording.durationMs) }}</span>
          <span>크기: {{ formatBytes(previewRecording.fileSize) }}</span>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped>
.page { padding: 16px 24px; }
.page-title { font-size: 1.5rem; font-weight: 600; margin-bottom: 12px; }
.alert-danger { padding: 8px 12px; background: #f8d7da; color: #721c24; border-radius: 4px; margin-bottom: 12px; }

.filters { background: #f8f9fa; padding: 12px; border-radius: 6px; margin-bottom: 16px; }
.filter-row { display: flex; flex-wrap: wrap; gap: 12px; align-items: end; }
.filter-row label { display: flex; flex-direction: column; font-size: 0.85rem; color: #495057; }
.filter-row input, .filter-row select { padding: 6px 8px; border: 1px solid #ced4da; border-radius: 4px; font-size: 0.9rem; }

.meta { color: #6c757d; font-size: 0.85rem; margin: 4px 0 8px; }

.recording-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
.recording-table th, .recording-table td { padding: 8px 10px; border-bottom: 1px solid #e9ecef; text-align: left; }
.recording-table th { background: #f8f9fa; font-weight: 600; }
.recording-table tbody tr:hover { background: #f1f3f5; }
.empty { text-align: center; color: #6c757d; padding: 24px; }

.actions { display: flex; gap: 4px; align-items: center; }

.status-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; color: white; font-size: 0.8rem; }

.btn { padding: 4px 10px; border: 1px solid transparent; border-radius: 4px; cursor: pointer; font-size: 0.85rem; text-decoration: none; display: inline-flex; align-items: center; }
.btn-sm { padding: 3px 8px; font-size: 0.8rem; }
.btn-primary { background: #0d6efd; color: white; border-color: #0d6efd; }
.btn-primary:hover { background: #0b5ed7; }
.btn-primary:disabled { background: #6c757d; cursor: not-allowed; }
.btn-outline { background: white; color: #495057; border-color: #ced4da; }
.btn-outline:hover { background: #e9ecef; }
.btn-outline:disabled { color: #adb5bd; cursor: not-allowed; }
.btn-danger { background: #dc3545; color: white; border-color: #dc3545; }
.btn-danger:hover { background: #bb2d3b; }
.btn-disabled { background: #e9ecef; color: #adb5bd; border-color: #dee2e6; cursor: not-allowed; }

.modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal { background: white; border-radius: 8px; width: min(90vw, 900px); padding: 16px; }
.modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.btn-close { background: transparent; border: none; cursor: pointer; font-size: 1.2rem; color: #6c757d; }
.preview-video { width: 100%; max-height: 60vh; background: #000; border-radius: 4px; }
.modal-meta { display: flex; gap: 16px; flex-wrap: wrap; margin-top: 8px; font-size: 0.85rem; color: #495057; }
</style>
